/**
 * Get the position for the tooltip based on target element and preferred position
 * Ensures the tooltip always stays within viewport bounds
 */
export const getTooltipPosition = (
  targetSelector: string,
  preferredPosition: "top" | "bottom" | "left" | "right" | "center"
): { top: number; left: number; position: "top" | "bottom" | "left" | "right" | "center" } => {
  const tooltipWidth = 360;
  const tooltipHeight = 300; // Slightly increased for safety
  const viewportPadding = 20; // Minimum distance from viewport edges
  const gap = 16;

  if (preferredPosition === "center" || !targetSelector) {
    return {
      top: window.innerHeight / 2,
      left: window.innerWidth / 2,
      position: "center",
    };
  }

  const element = document.querySelector(targetSelector);
  if (!element) {
    return {
      top: window.innerHeight / 2,
      left: window.innerWidth / 2,
      position: "center",
    };
  }

  const rect = element.getBoundingClientRect();

  // Helper to calculate final tooltip bounds after transform
  const getTransformedBounds = (pos: string, calcTop: number, calcLeft: number) => {
    switch (pos) {
      case "top":
        // transform: translate(-50%, -100%)
        return {
          top: calcTop - tooltipHeight,
          bottom: calcTop,
          left: calcLeft - tooltipWidth / 2,
          right: calcLeft + tooltipWidth / 2,
        };
      case "bottom":
        // transform: translateX(-50%)
        return {
          top: calcTop,
          bottom: calcTop + tooltipHeight,
          left: calcLeft - tooltipWidth / 2,
          right: calcLeft + tooltipWidth / 2,
        };
      case "left":
        // transform: translate(-100%, -50%)
        return {
          top: calcTop - tooltipHeight / 2,
          bottom: calcTop + tooltipHeight / 2,
          left: calcLeft - tooltipWidth,
          right: calcLeft,
        };
      case "right":
        // transform: translateY(-50%)
        return {
          top: calcTop - tooltipHeight / 2,
          bottom: calcTop + tooltipHeight / 2,
          left: calcLeft,
          right: calcLeft + tooltipWidth,
        };
      default:
        return { top: calcTop, bottom: calcTop + tooltipHeight, left: calcLeft, right: calcLeft + tooltipWidth };
    }
  };

  // Check if position would fit in viewport
  const wouldFitInViewport = (pos: string, calcTop: number, calcLeft: number): boolean => {
    const bounds = getTransformedBounds(pos, calcTop, calcLeft);
    return (
      bounds.top >= viewportPadding &&
      bounds.bottom <= window.innerHeight - viewportPadding &&
      bounds.left >= viewportPadding &&
      bounds.right <= window.innerWidth - viewportPadding
    );
  };

  // Try each position and check if it fits
  const tryPosition = (pos: "top" | "bottom" | "left" | "right"): { fits: boolean; top: number; left: number } => {
    let calcTop: number;
    let calcLeft: number;

    switch (pos) {
      case "top":
        calcTop = rect.top - gap;
        calcLeft = rect.left + rect.width / 2;
        break;
      case "bottom":
        calcTop = rect.bottom + gap;
        calcLeft = rect.left + rect.width / 2;
        break;
      case "left":
        calcTop = rect.top + rect.height / 2;
        calcLeft = rect.left - gap;
        break;
      case "right":
        calcTop = rect.top + rect.height / 2;
        calcLeft = rect.right + gap;
        break;
    }

    const fits = wouldFitInViewport(pos, calcTop, calcLeft);
    return { fits, top: calcTop, left: calcLeft };
  };

  // Priority order: preferred first, then alternatives
  const positionPriority: Record<string, ("top" | "bottom" | "left" | "right")[]> = {
    top: ["top", "bottom", "left", "right"],
    bottom: ["bottom", "top", "left", "right"],
    left: ["left", "right", "bottom", "top"],
    right: ["right", "left", "bottom", "top"],
  };

  const priorities = positionPriority[preferredPosition] || ["bottom", "top", "left", "right"];

  // Try each position in priority order
  for (const pos of priorities) {
    const result = tryPosition(pos);
    if (result.fits) {
      return { top: result.top, left: result.left, position: pos };
    }
  }

  // If nothing fits, use bottom and clamp to viewport
  const bottomPos = tryPosition("bottom");
  const bounds = getTransformedBounds("bottom", bottomPos.top, bottomPos.left);
  
  // Clamp to viewport
  let finalTop = bottomPos.top;
  let finalLeft = bottomPos.left;

  if (bounds.top < viewportPadding) {
    finalTop = viewportPadding + tooltipHeight / 2; // Account for translateY(-50%) if we switch
  }
  if (bounds.bottom > window.innerHeight - viewportPadding) {
    finalTop = window.innerHeight - viewportPadding - tooltipHeight;
  }
  if (bounds.left < viewportPadding) {
    finalLeft = viewportPadding + tooltipWidth / 2; // For translateX(-50%)
  }
  if (bounds.right > window.innerWidth - viewportPadding) {
    finalLeft = window.innerWidth - viewportPadding - tooltipWidth / 2;
  }

  return { top: finalTop, left: finalLeft, position: "bottom" };
};

